import React, { useState, useEffect } from 'react'
import {
  Card, Row, Col, Table, Button, Space, Statistic, Alert, Tag, DatePicker, Modal, App
} from 'antd'
import {
  ReloadOutlined, WarningOutlined, ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons'
import { monitoringApi } from '@/services/api'
import ReactECharts from 'echarts-for-react'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

interface ErrorStats {
  errorTypes: Array<{
    status_code: number
    count: number
    percentage: number
  }>
  errorTrends: Array<{
    hour: string
    error_count: number
  }>
  errorEndpoints: Array<{
    endpoint: string
    error_count: number
    last_error: number
  }>
}

const ErrorMonitoringPage: React.FC = () => {
  const { message } = App.useApp()
  const [errorStats, setErrorStats] = useState<ErrorStats | null>(null)
  const [errorLogs, setErrorLogs] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

  const [trendChartData, setTrendChartData] = useState<any>(null)
  const [typeChartData, setTypeChartData] = useState<any>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      console.log('🔄 开始获取错误监控数据...')

      const [statsResponse, logsResponse] = await Promise.all([
        monitoringApi.getErrorStats(),
        monitoringApi.getErrorLogs({
          limit: 50,
          startDate: dateRange?.[0]?.valueOf(),
          endDate: dateRange?.[1]?.valueOf()
        })
      ])

      console.log('📊 错误统计响应:', statsResponse)
      console.log('📋 错误日志响应:', logsResponse)

      if (statsResponse.success) {
        setErrorStats(statsResponse.data)
        updateCharts(statsResponse.data)
        console.log('✅ 错误统计数据设置成功')
      } else {
        console.warn('⚠️ 错误统计获取失败:', statsResponse.error)
        message.warning('错误统计获取失败')
      }

      if (logsResponse.success) {
        setErrorLogs(logsResponse.data || [])
        console.log('✅ 错误日志数据设置成功:', logsResponse.data?.length || 0)
      } else {
        console.warn('⚠️ 错误日志获取失败:', logsResponse.error)
        message.warning('错误日志获取失败')
      }
    } catch (error) {
      console.error('❌ 获取错误监控数据失败:', error)
      message.error('获取错误监控数据失败')
    } finally {
      setLoading(false)
    }
  }

  const updateCharts = (stats: ErrorStats) => {
    console.log('📈 更新图表数据:', stats)

    // 更新错误趋势图
    if (stats.errorTrends && stats.errorTrends.length > 0) {
      const trendOption = {
        title: {
          text: '错误趋势 (最近24小时)',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis'
        },
        xAxis: {
          type: 'category',
          data: stats.errorTrends.map(item =>
            dayjs(item.hour).format('HH:mm')
          )
        },
        yAxis: {
          type: 'value',
          name: '错误数量'
        },
        series: [
          {
            name: '错误数量',
            type: 'line',
            data: stats.errorTrends.map(item => item.error_count),
            itemStyle: { color: '#ff4d4f' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(255, 77, 79, 0.3)' },
                  { offset: 1, color: 'rgba(255, 77, 79, 0.1)' }
                ]
              }
            }
          }
        ]
      }
      setTrendChartData(trendOption)
      console.log('✅ 错误趋势图数据设置成功')
    } else {
      // 🔥 修复：设置空数据的默认图表
      setTrendChartData(null)
      console.log('📊 设置空的错误趋势图')
    }

    // 更新错误类型分布图
    if (stats.errorTypes && stats.errorTypes.length > 0) {
      const typeOption = {
        title: {
          text: '错误类型分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: '错误数量',
            type: 'pie',
            radius: '50%',
            data: stats.errorTypes.map(item => ({
              value: item.count,
              name: `HTTP ${item.status_code}`
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      setTypeChartData(typeOption)
      console.log('✅ 错误类型分布图数据设置成功')
    } else {
      // 🔥 修复：设置空数据的默认图表
      setTypeChartData(null)
      console.log('📊 设置空的错误类型分布图')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchData()
  }, [dateRange])

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 400 && statusCode < 500) return 'warning'
    if (statusCode >= 500) return 'error'
    return 'default'
  }

  const getStatusIcon = (statusCode: number) => {
    if (statusCode >= 400 && statusCode < 500) return <WarningOutlined />
    if (statusCode >= 500) return <CloseCircleOutlined />
    return <ExclamationCircleOutlined />
  }

  const columns = [
    {
      title: '状态码',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 100,
      render: (code: number) => (
        <Tag color={getStatusColor(code)} icon={getStatusIcon(code)}>
          {code}
        </Tag>
      )
    },
    {
      title: '端点',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method: string) => <Tag color="blue">{method}</Tag>
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (error: string) => (
        <span style={{ color: '#ff4d4f' }}>{error}</span>
      )
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (timestamp: number) => dayjs(timestamp).format('MM-DD HH:mm:ss')
    }
  ]

  const totalErrors = errorStats?.errorTypes.reduce((sum, item) => sum + item.count, 0) || 0
  const recentErrors = errorStats?.errorTrends.reduce((sum, item) => sum + item.error_count, 0) || 0
  const topErrorEndpoint = errorStats?.errorEndpoints[0]

  // 🔥 新增：同步数据功能
  const syncData = async () => {
    try {
      setLoading(true)
      const response = await monitoringApi.syncProxyData()
      if (response.success) {
        message.success('数据同步成功')
        fetchData()
      } else {
        message.error('数据同步失败')
      }
    } catch (error) {
      console.error('同步数据失败:', error)
      message.error('同步数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 🔥 新增：清空日志功能
  const clearLogs = async () => {
    try {
      // 🔥 修复：清空错误日志需要特殊处理，因为我们是按status_code >= 400查询的
      const response = await monitoringApi.clearLogs({
        target: 'database',
        logType: 'all', // 🔥 修复：先清空所有日志，然后重新同步
        olderThan: Date.now() - 24 * 60 * 60 * 1000 // 只清空24小时前的日志
      })
      if (response.success) {
        message.success('错误日志已清空')
        // 重新同步数据以获取最新的日志
        await syncData()
      } else {
        message.error('清空日志失败')
      }
    } catch (error) {
      console.error('清空日志失败:', error)
      message.error('清空日志失败')
    }
  }

  return (
    <div style={{ padding: '0' }}>
      <Card title="错误监控" extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始时间', '结束时间']}
            showTime
          />
          <Button icon={<ReloadOutlined />} onClick={syncData} loading={loading}>
            同步数据
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            刷新
          </Button>
          <Button
            danger
            onClick={() => {
              Modal.confirm({
                title: '确认清空',
                content: '确定要清空所有错误日志吗？此操作无法撤销。',
                onOk: clearLogs
              })
            }}
          >
            清空日志
          </Button>
        </Space>
      }>
        {/* 错误概览 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="总错误数"
              value={totalErrors}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="24小时错误"
              value={recentErrors}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<WarningOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="错误端点数"
              value={errorStats?.errorEndpoints.length || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="最高错误端点"
              value={topErrorEndpoint?.error_count || 0}
              valueStyle={{ color: '#f5222d' }}
              suffix={topErrorEndpoint ? `(${topErrorEndpoint.endpoint.split('/').pop()})` : ''}
            />
          </Col>
        </Row>

        {/* 错误警告 */}
        {recentErrors > 10 && (
          <Alert
            message="高错误率警告"
            description={`最近24小时内检测到 ${recentErrors} 个错误，请及时处理。`}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 图表展示 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="错误趋势" size="small">
              {trendChartData ? (
                <ReactECharts
                  option={trendChartData}
                  style={{ width: '100%', height: '300px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="错误类型分布" size="small">
              {typeChartData ? (
                <ReactECharts
                  option={typeChartData}
                  style={{ width: '100%', height: '300px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 错误端点统计 */}
        {errorStats?.errorEndpoints && errorStats.errorEndpoints.length > 0 && (
          <Card title="错误端点统计" size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={errorStats.errorEndpoints}
              pagination={false}
              size="small"
              rowKey="endpoint"
              columns={[
                {
                  title: '端点',
                  dataIndex: 'endpoint',
                  key: 'endpoint'
                },
                {
                  title: '错误次数',
                  dataIndex: 'error_count',
                  key: 'error_count',
                  width: 100,
                  align: 'right' as const,
                  render: (count: number) => (
                    <Tag color="red">{count}</Tag>
                  )
                },
                {
                  title: '最后错误时间',
                  dataIndex: 'last_error',
                  key: 'last_error',
                  width: 160,
                  render: (timestamp: number) => dayjs(timestamp).format('MM-DD HH:mm:ss')
                }
              ]}
            />
          </Card>
        )}

        {/* 最近错误日志 */}
        <Card title="最近错误日志" size="small">
          <Table
            columns={columns}
            dataSource={errorLogs}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: false
            }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </Card>
      </Card>
    </div>
  )
}

export default ErrorMonitoringPage
